import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Topbar } from '../components/Topbar'
import { StatCard } from '../components/StatCard'
import {
  Download,
  DollarSign,
  Loader2,
  Percent,
  Receipt,
  ShoppingBag,
} from 'lucide-react'
import { apiGet, formatMXN } from '../lib/api'

interface LibroVentasRow {
  orderNumber: string
  fecha: string
  cliente: string
  email: string
  rfc: string
  paymentStatus: string
  paymentMethod: string
  subtotal: number
  taxTotal: number
  discount: number
  shipping: number
  total: number
}

interface LibroVentasData {
  from: string
  to: string
  paymentStatus: string
  totals: {
    count: number
    sumSubtotal: number
    sumTaxTotal: number
    sumDiscount: number
    sumShipping: number
    sumTotal: number
  }
  rows: LibroVentasRow[]
}

const PAYMENT_STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'paid', label: 'Pagadas' },
  { value: 'pending', label: 'Pendientes' },
  { value: 'failed', label: 'Fallidas' },
  { value: 'refunded', label: 'Reembolsadas' },
  { value: 'all', label: 'Todas' },
]

const PAGE_SIZE = 50

function firstDayOfMonthISO(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`
}

function todayISO(): string {
  return new Date().toISOString().slice(0, 10)
}

function readInitialFilters() {
  if (typeof window === 'undefined') {
    return {
      from: firstDayOfMonthISO(),
      to: todayISO(),
      paymentStatus: 'paid',
      page: 1,
    }
  }
  const sp = new URLSearchParams(window.location.search)
  return {
    from: sp.get('from') || firstDayOfMonthISO(),
    to: sp.get('to') || todayISO(),
    paymentStatus: sp.get('paymentStatus') || 'paid',
    page: Math.max(1, parseInt(sp.get('page') || '1') || 1),
  }
}

export function LibroVentas() {
  const initial = useMemo(readInitialFilters, [])
  const [from, setFrom] = useState(initial.from)
  const [to, setTo] = useState(initial.to)
  const [paymentStatus, setPaymentStatus] = useState(initial.paymentStatus)
  const [page, setPage] = useState(initial.page)

  // Mantener URL linkeable.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const sp = new URLSearchParams()
    if (from) sp.set('from', from)
    if (to) sp.set('to', to)
    if (paymentStatus && paymentStatus !== 'paid') {
      sp.set('paymentStatus', paymentStatus)
    }
    if (page > 1) sp.set('page', String(page))
    const qs = sp.toString()
    const newUrl = `${window.location.pathname}${qs ? '?' + qs : ''}`
    window.history.replaceState(null, '', newUrl)
  }, [from, to, paymentStatus, page])

  const queryString = useMemo(() => {
    const sp = new URLSearchParams()
    if (from) sp.set('from', from)
    if (to) sp.set('to', to)
    if (paymentStatus) sp.set('paymentStatus', paymentStatus)
    return sp.toString()
  }, [from, to, paymentStatus])

  const { data, isLoading, isFetching, error } = useQuery<LibroVentasData>({
    queryKey: ['libro-ventas', from, to, paymentStatus],
    queryFn: () => apiGet(`/api/admin/reportes/libro-ventas?${queryString}`),
    placeholderData: (prev) => prev,
  })

  // Paginación cliente sobre `rows`.
  const totalRows = data?.rows.length ?? 0
  const totalPages = Math.max(1, Math.ceil(totalRows / PAGE_SIZE))
  const pageSafe = Math.min(page, totalPages)
  const visibleRows = useMemo(() => {
    if (!data) return []
    const start = (pageSafe - 1) * PAGE_SIZE
    return data.rows.slice(start, start + PAGE_SIZE)
  }, [data, pageSafe])

  // Reset de página si los filtros reducen el total por debajo de la pág actual.
  useEffect(() => {
    if (page > totalPages) setPage(1)
  }, [totalPages, page])

  function onExport() {
    const sp = new URLSearchParams()
    if (from) sp.set('from', from)
    if (to) sp.set('to', to)
    if (paymentStatus) sp.set('paymentStatus', paymentStatus)
    sp.set('format', 'xlsx')
    window.location.href = `/api/admin/reportes/libro-ventas?${sp.toString()}`
  }

  const subtitle = data
    ? `${data.totals.count.toLocaleString('es-MX')} ventas en el período`
    : 'Cargando…'

  return (
    <>
      <Topbar
        title="Libro de ventas"
        subtitle={subtitle}
        actions={
          <button
            onClick={onExport}
            disabled={!data || data.totals.count === 0}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
          >
            <Download className="size-3.5" /> Exportar Excel
          </button>
        }
      />

      <div className="p-6">
        {/* Filtros */}
        <div className="mb-4 flex flex-wrap items-end gap-3 rounded-2xl border border-ink-200 bg-white p-4">
          <div>
            <label className="mb-1 block text-xs font-medium text-ink-600">Desde</label>
            <input
              type="date"
              value={from}
              onChange={(e) => {
                setFrom(e.target.value)
                setPage(1)
              }}
              className="rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-sm outline-none focus:border-brand-500"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-ink-600">Hasta</label>
            <input
              type="date"
              value={to}
              onChange={(e) => {
                setTo(e.target.value)
                setPage(1)
              }}
              className="rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-sm outline-none focus:border-brand-500"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-ink-600">Estado pago</label>
            <select
              value={paymentStatus}
              onChange={(e) => {
                setPaymentStatus(e.target.value)
                setPage(1)
              }}
              className="rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-sm outline-none focus:border-brand-500"
            >
              {PAYMENT_STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {(error as Error).message}
          </div>
        )}

        {/* Stats */}
        {data && (
          <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              label="# Ventas"
              value={data.totals.count.toLocaleString('es-MX')}
              icon={ShoppingBag}
              color="blue"
            />
            <StatCard
              label="Subtotal"
              value={formatMXN(data.totals.sumSubtotal)}
              icon={Receipt}
              color="violet"
            />
            <StatCard
              label="IVA"
              value={formatMXN(data.totals.sumTaxTotal)}
              icon={Percent}
              color="amber"
            />
            <StatCard
              label="Total"
              value={formatMXN(data.totals.sumTotal)}
              icon={DollarSign}
              color="emerald"
            />
          </div>
        )}

        {/* Tabla */}
        <div className="overflow-hidden rounded-2xl border border-ink-200 bg-white">
          {isLoading && (
            <div className="grid place-items-center py-20 text-ink-500">
              <Loader2 className="size-8 animate-spin" />
              <p className="mt-3 text-sm">Generando libro de ventas…</p>
            </div>
          )}

          {data && (
            <>
              <div className="relative overflow-x-auto">
                {isFetching && (
                  <div className="absolute right-3 top-3 z-10 rounded-full bg-white p-1.5 shadow-md">
                    <Loader2 className="size-4 animate-spin text-brand-600" />
                  </div>
                )}
                <table className="w-full text-sm">
                  <thead className="bg-ink-50">
                    <tr className="text-left text-xs uppercase tracking-wider text-ink-500">
                      <th className="px-4 py-3 font-medium">Fecha</th>
                      <th className="px-4 py-3 font-medium">Pedido</th>
                      <th className="px-4 py-3 font-medium">Cliente</th>
                      <th className="px-4 py-3 font-medium text-right">Subtotal</th>
                      <th className="px-4 py-3 font-medium text-right">IVA</th>
                      <th className="px-4 py-3 font-medium text-right">Descuento</th>
                      <th className="px-4 py-3 font-medium text-right">Envío</th>
                      <th className="px-4 py-3 font-medium text-right">Total</th>
                      <th className="px-4 py-3 font-medium">Método</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRows.length === 0 && (
                      <tr>
                        <td
                          colSpan={9}
                          className="px-4 py-12 text-center text-sm text-ink-500"
                        >
                          No hay ventas en el rango seleccionado.
                        </td>
                      </tr>
                    )}
                    {visibleRows.map((r) => (
                      <tr
                        key={r.orderNumber}
                        className="border-t border-ink-100 transition-colors hover:bg-ink-50"
                      >
                        <td className="whitespace-nowrap px-4 py-3 text-ink-700">
                          {r.fecha}
                        </td>
                        <td className="px-4 py-3 font-mono text-sm font-medium text-ink-900">
                          #{r.orderNumber}
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-ink-900">
                            {r.cliente || '—'}
                          </div>
                          {r.rfc && (
                            <div className="text-xs text-ink-500">{r.rfc}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right text-ink-700">
                          {formatMXN(r.subtotal)}
                        </td>
                        <td className="px-4 py-3 text-right text-ink-700">
                          {formatMXN(r.taxTotal)}
                        </td>
                        <td className="px-4 py-3 text-right text-ink-700">
                          {r.discount > 0 ? formatMXN(r.discount) : '—'}
                        </td>
                        <td className="px-4 py-3 text-right text-ink-700">
                          {r.shipping > 0 ? formatMXN(r.shipping) : '—'}
                        </td>
                        <td className="px-4 py-3 text-right font-semibold text-ink-900">
                          {formatMXN(r.total)}
                        </td>
                        <td className="px-4 py-3 text-xs capitalize text-ink-700">
                          {r.paymentMethod || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {data.totals.count > 0 && (
                    <tfoot className="bg-ink-50">
                      <tr className="border-t border-ink-200 text-sm font-semibold text-ink-900">
                        <td className="px-4 py-3" colSpan={3}>
                          Totales del período
                        </td>
                        <td className="px-4 py-3 text-right">
                          {formatMXN(data.totals.sumSubtotal)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {formatMXN(data.totals.sumTaxTotal)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {formatMXN(data.totals.sumDiscount)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {formatMXN(data.totals.sumShipping)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {formatMXN(data.totals.sumTotal)}
                        </td>
                        <td className="px-4 py-3"></td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>

              {totalRows > PAGE_SIZE && (
                <div className="flex items-center justify-between border-t border-ink-200 bg-ink-50 px-4 py-3 text-sm">
                  <div className="text-ink-500">
                    Página <strong className="text-ink-900">{pageSafe}</strong> de{' '}
                    <strong className="text-ink-900">
                      {totalPages.toLocaleString('es-MX')}
                    </strong>{' '}
                    ({totalRows.toLocaleString('es-MX')} ventas)
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={pageSafe === 1}
                      className="rounded-md border border-ink-200 bg-white px-3 py-1 hover:bg-ink-100 disabled:opacity-50"
                    >
                      Anterior
                    </button>
                    <button className="rounded-md bg-brand-600 px-3 py-1 text-white">
                      {pageSafe}
                    </button>
                    <button
                      onClick={() => setPage((p) => p + 1)}
                      disabled={pageSafe >= totalPages}
                      className="rounded-md border border-ink-200 bg-white px-3 py-1 hover:bg-ink-100 disabled:opacity-50"
                    >
                      Siguiente
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  )
}
